import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  BookOpen,
  CheckCircle,
  AlertTriangle,
  Shield,
  DoorOpen,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import type { AdminDashboardPageContext } from './useAdminDashboardPage';

type VistaTorre = 'turno' | 'dia';

interface Props {
  ctx: AdminDashboardPageContext;
}

type TurmaRow = AdminDashboardPageContext['turmasPorTurno'][number]['turmas'][number];
type TurmaListaModo = 'todas' | 'criticas';

function statusAulaCurto(status: string, totalAlunos?: number): string {
  const s = status.toUpperCase();
  if (s === 'PENDENTE') return 'falta';
  if (s === 'CONCLUIDA') return 'ok';
  if (s === 'ABERTA') return (totalAlunos ?? 0) > 0 ? 'feita' : 'aberta';
  return s.toLowerCase().slice(0, 6);
}

function turmaEhCritica(t: TurmaRow) {
  return Boolean(t.critico ?? (t.temRegistro && t.pct != null && t.pct < 70));
}

function metricsTurma(t: TurmaRow) {
  const r = t.resumoChamada;
  const base = r && r.totalAlunos > 0 ? r.totalAlunos : t.alunos;
  const presentes = r ? r.totalPresentes : t.pres;
  const ausentes = r ? r.totalAusentes : t.aus;
  const atrasados = r?.totalAtrasados ?? 0;
  const justificados = r?.totalJustificados ?? 0;
  const pctPres =
    t.temRegistro && base > 0 ? Math.round((presentes / base) * 100) : null;
  const pctAus =
    t.temRegistro && base > 0 ? Math.round((ausentes / base) * 100) : null;
  return {
    base,
    presentes,
    ausentes,
    atrasados,
    justificados,
    pctPres,
    pctAus,
    numeroAula: r?.numeroAula ?? null,
  };
}

function textoResumoTurma(t: TurmaRow) {
  if (!t.temRegistro) return `${t.alunos} alunos · aguarda chamada`;
  const m = metricsTurma(t);
  const extras = [
    m.atrasados > 0 ? `${m.atrasados} atr.` : null,
    m.justificados > 0 ? `${m.justificados} just.` : null,
  ].filter(Boolean).join(' · ');
  const corpo = `${m.presentes} pres. · ${m.ausentes} aus.${extras ? ` · ${extras}` : ''} (${m.base})`;
  if (m.numeroAula != null) return `${m.numeroAula}ª aula: ${corpo}`;
  return corpo;
}

function MobileTurmasList({
  turmas,
  modoTurno,
  turnoLabel,
}: {
  turmas: TurmaRow[];
  modoTurno: boolean;
  turnoLabel: string | null;
}) {
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [modo, setModo] = useState<TurmaListaModo>('todas');
  const turmasKey = turmas.map((t) => t.id).join('|');
  const nCriticas = turmas.filter(turmaEhCritica).length;

  useEffect(() => {
    setAbertaId(null);
  }, [turmasKey, modo]);

  const lista = useMemo(() => {
    const base =
      modo === 'criticas' ? turmas.filter(turmaEhCritica) : [...turmas];
    if (modo === 'criticas') {
      base.sort(
        (a, b) =>
          (a.pct ?? 101) - (b.pct ?? 101) ||
          a.nome.localeCompare(b.nome, 'pt-BR'),
      );
    }
    return base.slice(0, modoTurno ? 99 : 12);
  }, [turmas, modo, modoTurno]);

  return (
    <div>
      <div className="text-[10px] font-bold text-slate-700 flex items-center gap-1.5 mb-1">
        <BookOpen size={12} className="text-blue-500" />{' '}
        {modoTurno ? `Turmas · ${turnoLabel}` : 'Turmas'}
      </div>
      <div className="flex gap-1.5 mb-1.5">
        <button
          type="button"
          className={`text-[8px] font-bold px-2 py-0.5 rounded-full border ${
            modo === 'todas'
              ? 'bg-slate-800 text-white border-slate-800'
              : 'bg-slate-50 text-slate-600 border-slate-200'
          }`}
          onClick={() => setModo('todas')}
        >
          Todas ({turmas.length})
        </button>
        <button
          type="button"
          className={`text-[8px] font-bold px-2 py-0.5 rounded-full border ${
            modo === 'criticas'
              ? 'bg-red-700 text-white border-red-700'
              : 'bg-slate-50 text-slate-600 border-slate-200'
          }`}
          onClick={() => setModo('criticas')}
        >
          Críticas ({nCriticas})
        </button>
      </div>
      <div className="max-h-[260px] overflow-auto">
        {lista.length === 0 ? (
          <div className="text-[8px] text-slate-400 py-2">
            {modo === 'criticas' ? 'Nenhuma turma crítica.' : 'Sem turmas.'}
          </div>
        ) : null}
        {lista.map((turma, i) => {
          const esperadas = turma.chamadasEsperadas ?? turma.aulas?.length ?? 0;
          const feitas = turma.chamadasFeitas ?? 0;
          const open = abertaId === turma.id;
          const aulas = turma.aulas ?? [];
          const critico = turmaEhCritica(turma);
          const m = metricsTurma(turma);
          const ausentesDia = turma.ausentesUnicosDia ?? [];
          const ultimaComChamada = [...aulas]
            .filter((a) => a.chamadaId && (a.status || '').toUpperCase() !== 'PENDENTE')
            .sort((a, b) => b.numeroAula - a.numeroAula)[0];
          const ausentesUltima = ultimaComChamada?.ausentes ?? [];
          return (
            <div
              key={turma.id}
              className={`border-b border-slate-100 last:border-0 ${critico ? 'bg-red-50/40' : ''}`}
            >
              <button
                type="button"
                className="w-full text-left flex items-center justify-between py-2 bg-transparent border-none cursor-pointer p-0"
                aria-expanded={open}
                onClick={() => setAbertaId((id) => (id === turma.id ? null : turma.id))}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold truncate">
                    {i + 1}. {turma.nome}
                    {esperadas > 0 ? (
                      <span className="ml-1 text-[8px] font-bold text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded">
                        {feitas}/{esperadas} chamadas
                      </span>
                    ) : null}
                    {turma.temRegistro ? (
                      <span className="ml-1 inline-flex items-center overflow-hidden rounded border border-slate-200 align-middle">
                        <span className="text-[8px] font-extrabold text-emerald-700 bg-emerald-50 px-1 py-0.5">
                          {m.pctPres}%
                        </span>
                        <span className="text-[8px] font-extrabold text-red-700 bg-red-50 px-1 py-0.5">
                          {m.pctAus ?? 0}% aus
                        </span>
                      </span>
                    ) : null}
                    {critico ? (
                      <span className="ml-1 text-[8px] font-bold text-red-700">crítico</span>
                    ) : null}
                  </div>
                  <div className="text-[8px] text-slate-500">
                    {textoResumoTurma(turma)}
                    {!modoTurno && turma.turnoNome ? ` · ${turma.turnoNome}` : ''}
                  </div>
                  {turma.temRegistro ? (
                    <div className="mt-1 h-1 rounded-full overflow-hidden bg-slate-100 flex">
                      <div
                        className="h-full bg-emerald-400"
                        style={{ width: `${Math.max(0, Math.min(100, m.pctPres ?? 0))}%` }}
                      />
                      <div
                        className="h-full bg-red-400"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100 - (m.pctPres ?? 0), m.pctAus ?? 0),
                          )}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </button>
              {open ? (
                <div className="pb-2 pl-2 space-y-1.5">
                  <div className="text-[8px] text-slate-500">
                    {textoResumoTurma(turma)}
                    {m.pctPres != null
                      ? ` · ${m.pctPres}% pres / ${m.pctAus}% aus`
                      : ''}
                    {esperadas > 0 ? ` · ${feitas}/${esperadas} aulas` : ''}
                  </div>
                  {ausentesUltima.length > 0 ? (
                    <div>
                      <div className="text-[8px] font-bold text-orange-800 mb-0.5">
                        Ausentes nesta {m.numeroAula != null ? `${m.numeroAula}ª` : 'última'} aula (
                        {ausentesUltima.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {ausentesUltima.map((a) => (
                          <span
                            key={a.id}
                            className="text-[7px] font-semibold bg-orange-100 text-orange-900 px-1 py-0.5 rounded"
                          >
                            {a.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {ausentesDia.length > 0 ? (
                    <div>
                      <div className="text-[8px] font-bold text-red-800 mb-0.5">
                        Ausentes em alguma aula do dia ({ausentesDia.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {ausentesDia.map((a) => (
                          <span
                            key={a.id}
                            className="text-[7px] font-semibold bg-red-100 text-red-800 px-1 py-0.5 rounded"
                          >
                            {a.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {aulas.length === 0 ? (
                    <div className="text-[8px] text-slate-400">Sem horários neste dia</div>
                  ) : (
                    aulas.map((a) => {
                      const ausentesAula = a.ausentes ?? [];
                      const st = (a.status || 'PENDENTE').toUpperCase();
                      const temCh = Boolean(a.chamadaId && st !== 'PENDENTE');
                      return (
                        <div
                          key={`${turma.id}-${a.numeroAula}-${a.chamadaId || 's'}`}
                          className="text-[8px] text-slate-600 border border-slate-100 rounded px-1.5 py-1"
                        >
                          <div className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                            <span className="font-bold">
                              {a.numeroAula > 0 ? `${a.numeroAula}ª` : '—'}
                            </span>
                            <span>
                              {a.horaInicio && a.horaFim
                                ? `${a.horaInicio}–${a.horaFim}`
                                : a.horaInicio || '—'}
                            </span>
                            <span>{a.materiaNome || 'Aula'}</span>
                            <span className="text-slate-400">
                              {a.professorNome
                                ? /^prof/i.test(a.professorNome)
                                  ? a.professorNome
                                  : `Prof. ${a.professorNome}`
                                : 's/ prof'}
                            </span>
                            <span className="font-bold uppercase text-slate-500">
                              {statusAulaCurto(a.status, a.totalAlunos)}
                            </span>
                          </div>
                          {temCh ? (
                            <div className="text-[7px] text-slate-500 mt-0.5">
                              {a.totalPresentes ?? 0} pres. ·{' '}
                              {a.totalAusentes ?? ausentesAula.length} aus.
                              {(a.totalAtrasados ?? 0) > 0
                                ? ` · ${a.totalAtrasados} atr.`
                                : ''}
                              {(a.totalJustificados ?? 0) > 0
                                ? ` · ${a.totalJustificados} just.`
                                : ''}
                            </div>
                          ) : null}
                          {ausentesAula.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {ausentesAula.map((al) => (
                                <span
                                  key={al.id}
                                  className="text-[7px] font-semibold bg-red-100 text-red-800 px-1 py-0.5 rounded"
                                >
                                  {al.nome}
                                </span>
                              ))}
                            </div>
                          ) : temCh ? (
                            <div className="text-[7px] text-emerald-600 font-semibold mt-0.5">
                              Sem ausentes
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                  <Link
                    to="/admin/chamada"
                    className="inline-block text-[9px] font-bold text-blue-600 mt-0.5"
                  >
                    Ver na Chamada
                  </Link>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminDashboardMobileView({ ctx }: Props) {
  const {
    nomeEscola,
    user,
    dataFormatada,
    dataCurta,
    diaReferenciaLabel,
    isSimulation,
    alunosAtivos,
    turmasAtivas,
    profsAtivos,
    censoPorTurno,
    presentesHoje,
    ausentesHoje,
    pctPresenca,
    semChamadaComPortaria,
    portariaCount,
    chamadasRealizadas,
    turmasChamadaTotal,
    inconsistencias,
    realtimeConnected,
    turmasPorTurno,
    semRegistroHoje,
    turnoOperacional,
    presentesTurno,
    ausentesTurno,
    baseAlunosTurno,
    semRegistroTurno,
    pctPresencaTurno,
    chamadasTurnoRealizadas,
    turmasTurnoTotal,
    portariaTurno,
    temRegistroTurno,
    inconsistenciasTurno,
  } = ctx;

  const temTurno = Boolean(turnoOperacional && baseAlunosTurno > 0);
  const [vista, setVista] = useState<VistaTorre>(temTurno ? 'turno' : 'dia');

  useEffect(() => {
    if (!temTurno && vista === 'turno') setVista('dia');
  }, [temTurno, vista]);

  const modoTurno = vista === 'turno' && temTurno;
  const grupoAtivo = turmasPorTurno.find((g) => g.isTurnoAtivo);

  const presentes = modoTurno ? presentesTurno : presentesHoje;
  const ausentes = modoTurno ? ausentesTurno : ausentesHoje;
  const semReg = modoTurno ? semRegistroTurno : semRegistroHoje;
  const base = modoTurno ? baseAlunosTurno : alunosAtivos;
  const pct = modoTurno ? pctPresencaTurno : pctPresenca;
  const portaria = modoTurno ? portariaTurno : portariaCount;
  const chamadas = modoTurno ? chamadasTurnoRealizadas : chamadasRealizadas;
  const chamadasTot = modoTurno ? turmasTurnoTotal : turmasChamadaTotal;
  const inconsist = modoTurno ? inconsistenciasTurno : inconsistencias;

  const donut = [
    { name: 'Presentes', value: Math.max(presentes, 0), color: '#10b981' },
    { name: 'Ausentes', value: ausentes, color: '#f59e0b' },
    ...(semReg > 0 ? [{ name: 'S/ chamada', value: semReg, color: '#94a3b8' }] : []),
  ].filter((d) => d.value > 0);

  const contexto = modoTurno
    ? `Turno ${turnoOperacional}. Dia da escola: ${pctPresenca != null ? `${pctPresenca}%` : '—'}.`
    : `Dia consolidado · ${presentesHoje}/${alunosAtivos} na chamada.`;

  return (
    <div className="flex-1 overflow-y-auto bg-[#f5f6f8] font-sans text-slate-800 p-2 pb-12">
      <div className="flex flex-col gap-3 max-w-[1600px] mx-auto">
        <div className="bg-[#2a4db3] rounded-xl text-white px-5 py-3 relative overflow-hidden flex justify-between items-center shadow-sm min-h-[84px]">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 min-w-0">
            <div className="text-[9px] uppercase font-bold text-blue-200/80 tracking-wider flex items-center gap-1.5 mb-1 bg-white/10 w-fit px-2 py-0.5 rounded">
              <Shield size={10} /> {user?.perfil === 'ESCOLA' ? 'UNIDADE ESCOLAR' : 'REDE MUNICIPAL'}
            </div>
            <h1 className="text-xl font-bold tracking-tight truncate">{nomeEscola || 'Escola'}</h1>
            <div className="text-[10px] text-blue-200 mt-0.5 flex items-center gap-2 font-medium flex-wrap">
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold ${
                  isSimulation ? 'bg-amber-500/25 text-amber-100' : 'bg-white/10 text-blue-100'
                }`}
              >
                {diaReferenciaLabel} · {dataCurta}
              </span>
              <span className="capitalize opacity-90">{dataFormatada}</span>
              {realtimeConnected ? (
                <span className="flex items-center gap-1 text-emerald-400 bg-emerald-900/30 px-1.5 py-0.5 rounded">
                  <span className="size-1 bg-emerald-400 rounded-full animate-pulse" /> Ao vivo
                </span>
              ) : null}
            </div>
            <p className="text-[11px] text-blue-100/95 mt-1.5 font-semibold leading-snug m-0">
              {contexto}
            </p>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center size-[52px] rounded-full border-[3px] border-blue-400/30 bg-blue-500/20 shadow-inner shrink-0">
            <span className="text-sm font-bold leading-none">
              {semChamadaComPortaria || pctPresenca === null ? '—' : `${pctPresenca}%`}
            </span>
            <span className="text-[5px] text-blue-200 uppercase font-bold mt-0.5">No dia</span>
          </div>
        </div>

        <div className="flex bg-slate-200/80 rounded-xl p-1 gap-1">
          <button
            type="button"
            disabled={!temTurno}
            onClick={() => setVista('turno')}
            className={`flex-1 text-[11px] font-extrabold py-2 rounded-lg ${
              modoTurno ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            } disabled:opacity-40`}
          >
            Turno{temTurno ? `: ${turnoOperacional}` : ''}
          </button>
          <button
            type="button"
            onClick={() => setVista('dia')}
            className={`flex-1 text-[11px] font-extrabold py-2 rounded-lg ${
              !modoTurno ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            }`}
          >
            Hoje (dia)
          </button>
        </div>

        <section
          className={`bg-white rounded-xl p-3 shadow-sm space-y-2 border ${
            modoTurno ? 'border-blue-100' : 'border-slate-200/80'
          }`}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-[12px] font-extrabold m-0">
              {modoTurno ? `Operação · ${turnoOperacional}` : 'Operação · dia'}
            </h2>
            <span
              className={`text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                modoTurno ? 'text-blue-700 bg-blue-50' : 'text-slate-500 bg-slate-100'
              }`}
            >
              {modoTurno ? 'turno' : 'escola · dia'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-slate-100 p-2">
              <div className="text-[9px] text-indigo-600 font-bold uppercase flex items-center gap-1">
                <DoorOpen size={10} /> RFID
              </div>
              <div className="text-lg font-extrabold mt-1">
                {portaria != null ? portaria : '—'}
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 p-2">
              <div className="text-[9px] text-emerald-600 font-bold uppercase flex items-center gap-1">
                <CheckCircle size={10} /> Chamadas
              </div>
              <div className="text-lg font-extrabold mt-1">
                {chamadas}
                <span className="text-xs text-slate-400 font-semibold">/{chamadasTot}</span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 p-2">
              <div className="text-[9px] text-blue-600 font-bold uppercase">Na chamada</div>
              <div className="text-lg font-extrabold mt-1">
                {pct == null || (modoTurno && !temRegistroTurno) ? '—' : `${pct}%`}
              </div>
              <div className="text-[9px] text-slate-400">
                {presentes}/{base}
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 p-2">
              <div className="text-[9px] text-orange-500 font-bold uppercase flex items-center gap-1">
                <AlertTriangle size={10} /> Inconsist.
              </div>
              <div className="text-lg font-extrabold mt-1">{inconsist}</div>
            </div>
          </div>

          <p className="text-[10px] text-slate-500 m-0 font-medium">
            Chamada = professor · RFID = portaria (fontes diferentes)
            {modoTurno && pctPresenca != null
              ? ` · No dia: ${pctPresenca}%`
              : temTurno && pctPresencaTurno != null
                ? ` · Turno ${turnoOperacional}: ${pctPresencaTurno}%`
                : ''}
          </p>

          {donut.length > 0 ? (
            <div className="flex items-center gap-3">
              <div className="w-[72px] h-[72px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donut}
                      innerRadius={22}
                      outerRadius={34}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {donut.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-slate-600 space-y-1 font-medium">
                <div>Na chamada: <strong>{presentes}</strong></div>
                <div>Ausentes: <strong>{ausentes}</strong></div>
                <div>S/ chamada: <strong>{semReg}</strong></div>
              </div>
            </div>
          ) : null}

          <MobileTurmasList
            turmas={
              modoTurno
                ? grupoAtivo?.turmas ?? []
                : turmasPorTurno.flatMap((g) => g.turmas)
            }
            modoTurno={modoTurno}
            turnoLabel={turnoOperacional}
          />
        </section>

        <div className="bg-white rounded-lg border p-2 space-y-2">
          <div className="text-[9px] font-bold uppercase text-slate-500 tracking-wide">
            Censo
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
            <div>
              <strong className="block text-base">{alunosAtivos}</strong> Alunos
            </div>
            <div>
              <strong className="block text-base">{turmasAtivas}</strong> Turmas
            </div>
            <div>
              <strong className="block text-base">{profsAtivos}</strong> Profs
            </div>
          </div>
          {censoPorTurno.length > 0 ? (
            <div className="space-y-1.5 border-t border-slate-100 pt-2">
              <div className="text-[8px] font-bold uppercase text-slate-400">
                Por turno
              </div>
              {censoPorTurno.map((row) => (
                <div
                  key={row.turnoId ?? 'sem-turno'}
                  className={`flex items-center justify-between gap-2 rounded px-1.5 py-1 text-[8px] ${
                    row.isTurnoAtivo ? 'bg-blue-50 text-blue-900' : 'bg-slate-50 text-slate-600'
                  }`}
                >
                  <span className="font-bold truncate">
                    {row.turnoNome}
                    {row.isTurnoAtivo ? ' · ativo' : ''}
                  </span>
                  <span className="shrink-0 font-semibold">
                    {row.alunos} al. · {row.turmas} tur.
                    {row.professores > 0 ? ` · ${row.professores} pr.` : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <Link
          to="/admin/chamada"
          className="block text-center py-3 rounded-xl bg-blue-600 text-white text-sm font-bold"
        >
          Ir para chamada
        </Link>
      </div>
    </div>
  );
}
